/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  PatchedTFADeviceSetupRequest,
  PatchedTFADeviceWalletExtensionRequest,
  TFADeviceCheck,
  TFADeviceCheckInRequest,
  TFADeviceInitializationRequest,
  TFADeviceSetup,
  TFARequest,
  TFARequestPreDecide,
  TFARequestPreDecideRequest,
  TFARequestRequest,
} from '../models/index';
import {
    PatchedTFADeviceSetupRequestFromJSON,
    PatchedTFADeviceSetupRequestToJSON,
    PatchedTFADeviceWalletExtensionRequestFromJSON,
    PatchedTFADeviceWalletExtensionRequestToJSON,
    TFADeviceCheckFromJSON,
    TFADeviceCheckToJSON,
    TFADeviceCheckInRequestFromJSON,
    TFADeviceCheckInRequestToJSON,
    TFADeviceInitializationRequestFromJSON,
    TFADeviceInitializationRequestToJSON,
    TFADeviceSetupFromJSON,
    TFADeviceSetupToJSON,
    TFARequestFromJSON,
    TFARequestToJSON,
    TFARequestPreDecideFromJSON,
    TFARequestPreDecideToJSON,
    TFARequestPreDecideRequestFromJSON,
    TFARequestPreDecideRequestToJSON,
    TFARequestRequestFromJSON,
    TFARequestRequestToJSON,
} from '../models/index';

export interface TwoFactorAuthDeviceCheckInCreateRequest {
    tFADeviceCheckInRequest: TFADeviceCheckInRequest;
}

export interface TwoFactorAuthDeviceInitializationCreateRequest {
    tFADeviceInitializationRequest?: TFADeviceInitializationRequest;
}

export interface TwoFactorAuthDevicePartialUpdateRequest {
    activationToken: string;
    patchedTFADeviceSetupRequest?: PatchedTFADeviceSetupRequest;
}

export interface TwoFactorAuthDeviceRetrieveRequest {
    activationToken: string;
}

export interface TwoFactorAuthDeviceWalletExtensionPartialUpdateRequest {
    patchedTFADeviceWalletExtensionRequest?: PatchedTFADeviceWalletExtensionRequest;
}

export interface TwoFactorAuthRequestsCancelCreateRequest {
    id: string;
}

export interface TwoFactorAuthRequestsPreResultCreateRequest {
    id: string;
    tFARequestPreDecideRequest: TFARequestPreDecideRequest;
}

export interface TwoFactorAuthRequestsResultCreateRequest {
    id: string;
    tFARequestRequest: TFARequestRequest;
}

/**
 * 
 */
export class TwoFactorAuthApi extends runtime.BaseAPI {

    /**
     * Report app version & push notification token (APN or Firebase).  Requires authentication via device auth token.
     */
    async twoFactorAuthDeviceCheckInCreateRaw(requestParameters: TwoFactorAuthDeviceCheckInCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['tFADeviceCheckInRequest'] == null) {
            throw new runtime.RequiredError(
                'tFADeviceCheckInRequest',
                'Required parameter "tFADeviceCheckInRequest" was null or undefined when calling twoFactorAuthDeviceCheckInCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/device/check-in/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: TFADeviceCheckInRequestToJSON(requestParameters['tFADeviceCheckInRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Report app version & push notification token (APN or Firebase).  Requires authentication via device auth token.
     */
    async twoFactorAuthDeviceCheckInCreate(requestParameters: TwoFactorAuthDeviceCheckInCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceCheckInCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Finalize the setup of the device. From this point forward, only the device-specific authentication can be used to interact with the two-factor API.  Requires authentication via device auth token. This must be called by the client AFTER wallet extension.  This call is idempotent.
     */
    async twoFactorAuthDeviceConfirmationCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/device/confirmation/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Finalize the setup of the device. From this point forward, only the device-specific authentication can be used to interact with the two-factor API.  Requires authentication via device auth token. This must be called by the client AFTER wallet extension.  This call is idempotent.
     */
    async twoFactorAuthDeviceConfirmationCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceConfirmationCreateRaw(initOverrides);
    }

    /**
     * (Re-)initialize a two-factor device for the user. Will send an email with an onboarding link. Pass wallet_needed = True if mobile wallet should be set up.
     */
    async twoFactorAuthDeviceInitializationCreateRaw(requestParameters: TwoFactorAuthDeviceInitializationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/two-factor-auth/device/initialization/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: TFADeviceInitializationRequestToJSON(requestParameters['tFADeviceInitializationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * (Re-)initialize a two-factor device for the user. Will send an email with an onboarding link. Pass wallet_needed = True if mobile wallet should be set up.
     */
    async twoFactorAuthDeviceInitializationCreate(requestParameters: TwoFactorAuthDeviceInitializationCreateRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceInitializationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Using activation_token & account password, retrieve a long-lived per-device authentication token as well as a shared secret.  This call is idempotent.
     */
    async twoFactorAuthDevicePartialUpdateRaw(requestParameters: TwoFactorAuthDevicePartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TFADeviceSetup>> {
        if (requestParameters['activationToken'] == null) {
            throw new runtime.RequiredError(
                'activationToken',
                'Required parameter "activationToken" was null or undefined when calling twoFactorAuthDevicePartialUpdate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/device/{activation_token}/`.replace(`{${"activation_token"}}`, encodeURIComponent(String(requestParameters['activationToken']))),
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedTFADeviceSetupRequestToJSON(requestParameters['patchedTFADeviceSetupRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => TFADeviceSetupFromJSON(jsonValue));
    }

    /**
     * Using activation_token & account password, retrieve a long-lived per-device authentication token as well as a shared secret.  This call is idempotent.
     */
    async twoFactorAuthDevicePartialUpdate(requestParameters: TwoFactorAuthDevicePartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TFADeviceSetup> {
        const response = await this.twoFactorAuthDevicePartialUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Send mail_reset_2fa
     */
    async twoFactorAuthDeviceResendResetMailCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/two-factor-auth/device/resend-reset-mail/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Send mail_reset_2fa
     */
    async twoFactorAuthDeviceResendResetMailCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceResendResetMailCreateRaw(initOverrides);
    }

    /**
     * Get information about the device.
     */
    async twoFactorAuthDeviceRetrieveRaw(requestParameters: TwoFactorAuthDeviceRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TFADeviceCheck>> {
        if (requestParameters['activationToken'] == null) {
            throw new runtime.RequiredError(
                'activationToken',
                'Required parameter "activationToken" was null or undefined when calling twoFactorAuthDeviceRetrieve().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/device/{activation_token}/`.replace(`{${"activation_token"}}`, encodeURIComponent(String(requestParameters['activationToken']))),
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => TFADeviceCheckFromJSON(jsonValue));
    }

    /**
     * Get information about the device.
     */
    async twoFactorAuthDeviceRetrieve(requestParameters: TwoFactorAuthDeviceRetrieveRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TFADeviceCheck> {
        const response = await this.twoFactorAuthDeviceRetrieveRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Resets the pairing of the device. Afterwards a new device can be initialized again.
     */
    async twoFactorAuthDeviceUnpairCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/two-factor-auth/device/unpair/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Resets the pairing of the device. Afterwards a new device can be initialized again.
     */
    async twoFactorAuthDeviceUnpairCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceUnpairCreateRaw(initOverrides);
    }

    /**
     * Add information about public keys for which the private keys are stored on the device.  This call is idempotent.
     */
    async twoFactorAuthDeviceWalletExtensionPartialUpdateRaw(requestParameters: TwoFactorAuthDeviceWalletExtensionPartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/device/wallet-extension/`,
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedTFADeviceWalletExtensionRequestToJSON(requestParameters['patchedTFADeviceWalletExtensionRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Add information about public keys for which the private keys are stored on the device.  This call is idempotent.
     */
    async twoFactorAuthDeviceWalletExtensionPartialUpdate(requestParameters: TwoFactorAuthDeviceWalletExtensionPartialUpdateRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthDeviceWalletExtensionPartialUpdateRaw(requestParameters, initOverrides);
    }

    /**
     * Operations on two-factor requests.
     */
    async twoFactorAuthRequestsCancelCreateRaw(requestParameters: TwoFactorAuthRequestsCancelCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling twoFactorAuthRequestsCancelCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/two-factor-auth/requests/{id}/cancel/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Operations on two-factor requests.
     */
    async twoFactorAuthRequestsCancelCreate(requestParameters: TwoFactorAuthRequestsCancelCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.twoFactorAuthRequestsCancelCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Operations on two-factor requests.
     */
    async twoFactorAuthRequestsListRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<TFARequest>>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/requests/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(TFARequestFromJSON));
    }

    /**
     * Operations on two-factor requests.
     */
    async twoFactorAuthRequestsList(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<TFARequest>> {
        const response = await this.twoFactorAuthRequestsListRaw(initOverrides);
        return await response.value();
    }

    /**
     * Used by client to signal that they will probably accept the request very close in time afterwards. This is supposed to be called when user clicks on the confirm button, in case the client needs extra information to produce the final signed answer.  This call is idempotent, but might return a different result each time it is called.
     */
    async twoFactorAuthRequestsPreResultCreateRaw(requestParameters: TwoFactorAuthRequestsPreResultCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TFARequestPreDecide>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling twoFactorAuthRequestsPreResultCreate().'
            );
        }

        if (requestParameters['tFARequestPreDecideRequest'] == null) {
            throw new runtime.RequiredError(
                'tFARequestPreDecideRequest',
                'Required parameter "tFARequestPreDecideRequest" was null or undefined when calling twoFactorAuthRequestsPreResultCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/requests/{id}/pre-result/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: TFARequestPreDecideRequestToJSON(requestParameters['tFARequestPreDecideRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => TFARequestPreDecideFromJSON(jsonValue));
    }

    /**
     * Used by client to signal that they will probably accept the request very close in time afterwards. This is supposed to be called when user clicks on the confirm button, in case the client needs extra information to produce the final signed answer.  This call is idempotent, but might return a different result each time it is called.
     */
    async twoFactorAuthRequestsPreResultCreate(requestParameters: TwoFactorAuthRequestsPreResultCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TFARequestPreDecide> {
        const response = await this.twoFactorAuthRequestsPreResultCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Accept or deny a two-factor request.  This call is idempotent.
     */
    async twoFactorAuthRequestsResultCreateRaw(requestParameters: TwoFactorAuthRequestsResultCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<TFARequest>> {
        if (requestParameters['id'] == null) {
            throw new runtime.RequiredError(
                'id',
                'Required parameter "id" was null or undefined when calling twoFactorAuthRequestsResultCreate().'
            );
        }

        if (requestParameters['tFARequestRequest'] == null) {
            throw new runtime.RequiredError(
                'tFARequestRequest',
                'Required parameter "tFARequestRequest" was null or undefined when calling twoFactorAuthRequestsResultCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        if (this.configuration && this.configuration.accessToken) {
            const token = this.configuration.accessToken;
            const tokenString = await token("tfaBearerAuth", []);

            if (tokenString) {
                headerParameters["Authorization"] = `Bearer ${tokenString}`;
            }
        }
        const response = await this.request({
            path: `/api/two-factor-auth/requests/{id}/result/`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters['id']))),
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: TFARequestRequestToJSON(requestParameters['tFARequestRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => TFARequestFromJSON(jsonValue));
    }

    /**
     * Accept or deny a two-factor request.  This call is idempotent.
     */
    async twoFactorAuthRequestsResultCreate(requestParameters: TwoFactorAuthRequestsResultCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<TFARequest> {
        const response = await this.twoFactorAuthRequestsResultCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

}
